/**
 * THE HOUSEHOLD'S TIMELINE (ops #84, `docs/plan-client-workplace.md` sections 3.1 and 12, phase 1): one
 * list, newest first, of everything the record holds about the household, in place of the history's
 * five. The owner's words of 2026-10-22: "so we see the whole timeline with filters, so we can
 * understand what was happening", and a "Client view" checkbox "so operator can see client perspective
 * filtering out internal events".
 *
 * THE SERVER WRITES EVERY WORD. A row arrives with its title, its detail, who did it, its pill and its
 * tone, and whether the client saw it, decided once on the server by its kind and state
 * (`api/src/records/timeline.ts`); this screen draws them and decides none of them. The client view is
 * the server's too: the tick asks for `view=client`, and hiding a row here would be UX and never the
 * control.
 *
 * THE FILTERS LIVE IN THE ADDRESS: the chips by kind, the home, the two days and the client view, so a
 * filtered timeline pasted into an issue opens as it was, and the way back from a sign-in keeps them.
 * No chip pressed is every kind.
 *
 * A PAGE AT A TIME. "Load more" asks for the page beneath the last row's cursor and adds it beneath; a
 * filter changed starts again from the newest.
 *
 * A call the phone system placed to test the line can still be thrown off the page, as it could from
 * the history's list of calls: quiet, under its row, in the office view alone.
 *
 * ON A HOME'S PAGE (phase 2, section 5) THE HOME IS FIXED: every read asks for that home whatever the
 * address says, the Home select gives way to the Household chip, off until pressed, which adds the
 * household's own rows (calls, texts tied to no clean, consent, marks, doors) beside the home's.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  TIMELINE_KINDS,
  TIMELINE_KIND_LABELS,
  type TimelineKind,
  type TimelineView,
} from '@fk/shared';
import {
  ApiError,
  discardClientCall,
  getClientTimeline,
  needsStepUp,
  type Discard,
  type TimelineFilters,
  type TimelinePage,
  type TimelineRow,
} from '../api.ts';
import { formatInstant, isDay, mediumDay } from '../format.ts';
import { Pill } from './AppShell.tsx';
import { DiscardControl } from './Discard.tsx';
import { Button, TARGET_SIZE } from './Field.tsx';
import { Busy, ErrorNotice, SavedNotice, StepUpNotice } from './Notice.tsx';

/** The address's word for each filter, so a pasted link reads as what it narrows. */
const PARAM = {
  kinds: 'kinds',
  home: 'home',
  household: 'household',
  from: 'from',
  to: 'to',
  view: 'view',
} as const;

const KNOWN: readonly string[] = TIMELINE_KINDS;

/** The filters an address carries, each word it does not know left out rather than sent. */
export function timelineFiltersFrom(search: URLSearchParams): TimelineFilters {
  const kinds = (search.get(PARAM.kinds) ?? '')
    .split(',')
    .map((kind) => kind.trim())
    .filter((kind): kind is TimelineKind => KNOWN.includes(kind));
  const day = (name: string) => {
    const value = search.get(name) ?? '';
    return isDay(value) ? value : '';
  };
  return {
    kinds: [...new Set(kinds)],
    home: search.get(PARAM.home) ?? '',
    household: search.get(PARAM.household) === '1',
    from: day(PARAM.from),
    to: day(PARAM.to),
    view: search.get(PARAM.view) === 'client' ? 'client' : 'office',
  };
}

/** Whether an address narrows the timeline at all, so a pasted link opens it at once. */
export function narrowsTheTimeline(search: URLSearchParams): boolean {
  return Object.values(PARAM).some((name) => search.has(name));
}

/** The filters written back into the address, every other word in it left as it was. */
function withFilters(search: URLSearchParams, filters: TimelineFilters): URLSearchParams {
  const params = new URLSearchParams(search);
  const put = (name: string, value: string) => {
    if (value === '') params.delete(name);
    else params.set(name, value);
  };
  put(PARAM.kinds, filters.kinds.join(','));
  put(PARAM.home, filters.home);
  put(PARAM.household, filters.household ? '1' : '');
  put(PARAM.from, filters.from);
  put(PARAM.to, filters.to);
  put(PARAM.view, filters.view === 'client' ? 'client' : '');
  return params;
}

export function Timeline({
  clientId,
  homeId = null,
}: {
  clientId: string;
  /** The home a home's page fixes the timeline to, or null on the household's page. */
  homeId?: string | null;
}) {
  const [search, setSearch] = useSearchParams();
  const place = useLocation();
  const filters = useMemo(() => {
    const read = timelineFiltersFrom(search);
    // A home's page asks for its own home whatever the address names; the household's page has no
    // Household chip, every row of the household being on it already.
    return homeId === null ? { ...read, household: false } : { ...read, home: homeId };
  }, [search, homeId]);
  /** One string for the filters, so the read is asked again exactly when one of them changes. */
  const asking = JSON.stringify(filters);
  const returnPath = `${place.pathname}${place.search}`;

  const [page, setPage] = useState<TimelinePage | null>(null);
  const [rows, setRows] = useState<readonly TimelineRow[]>([]);
  const [next, setNext] = useState<string | null>(null);
  /** The household's homes, kept while a page loads so the Home filter never empties under a finger. */
  const [homes, setHomes] = useState<TimelinePage['homes']>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [more, setMore] = useState<'idle' | 'asking' | ApiError>('idle');
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  /**
   * WHICH FIRST PAGE IS ON THE SCREEN, counted up each time one is asked for. A page beneath it is
   * added only while the count it was asked under still stands: Load more pressed, then a filter
   * changed before its answer came, would otherwise lay the old filters' rows, and their cursor,
   * beneath the new ones, an office row under "Client view" among them.
   */
  const generation = useRef(0);

  useEffect(() => {
    let live = true;
    generation.current += 1;
    setError(null);
    setPage(null);
    setMore('idle');
    getClientTimeline(clientId, JSON.parse(asking) as TimelineFilters, null, returnPath)
      .then((answer) => {
        if (!live) return;
        setPage(answer);
        setRows(answer.rows);
        setNext(answer.next);
        setHomes(answer.homes);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The timeline did not load.'),
        );
      });
    return () => {
      live = false;
    };
    // The way back is read when the page is asked for, and moves with the filters it carries.
  }, [clientId, asking, attempt]);

  const change = useCallback(
    (to: Partial<TimelineFilters>) => {
      setSearch((current) => withFilters(current, { ...timelineFiltersFrom(current), ...to }), {
        replace: true,
      });
    },
    [setSearch],
  );

  const loadMore = () => {
    if (next === null) return;
    const askedUnder = generation.current;
    setMore('asking');
    getClientTimeline(clientId, filters, next, returnPath)
      .then((answer) => {
        if (askedUnder !== generation.current) return;
        setRows((shown) => [...shown, ...answer.rows]);
        setNext(answer.next);
        setMore('idle');
      })
      .catch((thrown: unknown) => {
        if (askedUnder !== generation.current) return;
        setMore(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The next page did not load.'),
        );
      });
  };

  /** A call thrown away is gone from the record, so the page is asked for again from the top. */
  const discarded = (sentence: string) => {
    setSaid(sentence);
    setAttempt((n) => n + 1);
  };

  return (
    <div className="flex flex-col gap-3">
      <Filters filters={filters} homes={homes} fixedHome={homeId !== null} onChange={change} />
      {said ? <SavedNotice>{said}</SavedNotice> : null}
      {error ? (
        needsStepUp(error) ? (
          <StepUpNotice error={error} />
        ) : (
          <ErrorNotice
            title="The timeline did not load"
            message={error.message}
            onRetry={() => setAttempt((n) => n + 1)}
          />
        )
      ) : page === null ? (
        <Busy what="the timeline" />
      ) : rows.length === 0 ? (
        // A first page can come back empty with older rows still to read, so the sentence says
        // nothing matches only once the server says there is nothing beneath.
        <p className="text-sm text-navy/70">
          {next === null
            ? 'Nothing on the record matches these filters.'
            : 'Nothing matches in the newest stretch of the record. Load more looks further back.'}
        </p>
      ) : (
        <ol aria-label="Timeline" className="flex flex-col">
          {rows.map((row) => (
            <Line
              key={`${row.kind}:${row.id}`}
              row={row}
              clientId={clientId}
              view={filters.view}
              onDiscarded={discarded}
            />
          ))}
        </ol>
      )}
      {page !== null && error === null && next !== null ? (
        <div className="flex flex-col gap-2">
          {more instanceof ApiError ? (
            <ErrorNotice title="The next page did not load" message={more.message} />
          ) : null}
          <div>
            <Button tone="quiet" onClick={loadMore} disabled={more === 'asking'}>
              Load more
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The filters.
 * ------------------------------------------------------------------ */

const CAPTION = 'text-xs font-medium tracking-wide text-navy/70 uppercase';
const BOX = `rounded-lg border border-navy/25 bg-parchment px-3 text-navy ${TARGET_SIZE}`;

function Filters({
  filters,
  homes,
  fixedHome,
  onChange,
}: {
  filters: TimelineFilters;
  homes: TimelinePage['homes'];
  /** A home's page: no Home select, and the Household chip in its place. */
  fixedHome: boolean;
  onChange: (to: Partial<TimelineFilters>) => void;
}) {
  const toggle = (kind: TimelineKind) =>
    onChange({
      kinds: filters.kinds.includes(kind)
        ? filters.kinds.filter((one) => one !== kind)
        : [...filters.kinds, kind],
    });
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-navy/12 bg-parchment p-3">
      <div role="group" aria-label="Kinds of row" className="flex flex-wrap gap-1.5">
        {TIMELINE_KINDS.map((kind) => {
          const on = filters.kinds.includes(kind);
          return (
            <button
              key={kind}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(kind)}
              className={`rounded-full border px-3 py-1 text-xs ${
                on ? 'border-navy bg-navy text-cream' : 'border-navy/25 bg-cream text-navy'
              }`}
            >
              {TIMELINE_KIND_LABELS[kind]}
            </button>
          );
        })}
      </div>
      {fixedHome ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={filters.household}
            onClick={() => onChange({ household: !filters.household })}
            className={`rounded-full border px-3 py-1 text-xs ${
              filters.household
                ? 'border-navy bg-navy text-cream'
                : 'border-navy/25 bg-cream text-navy'
            }`}
          >
            Household
          </button>
          <span className="text-xs leading-4 text-navy/65">
            The household&apos;s own rows beside this home&apos;s: calls, texts tied to no clean,
            consent, marks and doors onto home.
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        {fixedHome ? null : (
          <div className="flex flex-col gap-1">
            <label htmlFor="timeline-home" className={CAPTION}>
              Home
            </label>
            <select
              id="timeline-home"
              value={filters.home}
              onChange={(event) => onChange({ home: event.target.value })}
              className={BOX}
            >
              <option value="">Every home</option>
              {homes.map((home) => (
                <option key={home.id} value={home.id}>
                  {home.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor="timeline-from" className={CAPTION}>
            From
          </label>
          <input
            id="timeline-from"
            type="date"
            value={filters.from}
            onChange={(event) => onChange({ from: event.target.value })}
            className={BOX}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="timeline-to" className={CAPTION}>
            To
          </label>
          <input
            id="timeline-to"
            type="date"
            value={filters.to}
            onChange={(event) => onChange({ to: event.target.value })}
            className={BOX}
          />
        </div>
      </div>
      <label
        htmlFor="timeline-client-view"
        className={`flex items-center gap-3 text-sm font-medium ${TARGET_SIZE}`}
      >
        <input
          id="timeline-client-view"
          type="checkbox"
          checked={filters.view === 'client'}
          onChange={(event) =>
            onChange({ view: (event.target.checked ? 'client' : 'office') as TimelineView })
          }
          className="h-5 w-5 shrink-0 accent-navy"
        />
        <span>Client view: only what the client saw or did</span>
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * One row.
 * ------------------------------------------------------------------ */

function Line({
  row,
  clientId,
  view,
  onDiscarded,
}: {
  row: TimelineRow;
  clientId: string;
  view: TimelineView;
  onDiscarded: (sentence: string) => void;
}) {
  // A row the record holds as a day alone is said as that day: its `at` is the day's first moment,
  // there only to order it, and nobody paid at midnight. The server marks it; nothing guesses from 00:00.
  const when = row.on ? mediumDay(row.on) : formatInstant(row.at);
  const inner = (
    <>
      <div className="flex min-w-0 grow flex-col gap-px">
        <span data-when="" className="text-xs leading-4 text-navy/60">
          {row.who ? `${when}, ${row.who.name}` : when}
        </span>
        <span data-title="" className="text-sm leading-5 font-medium">
          {row.title}
        </span>
        {row.detail ? <span className="text-xs leading-4 text-navy/65">{row.detail}</span> : null}
      </div>
      {row.state ? (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Pill tone={row.tone}>{row.state}</Pill>
        </div>
      ) : null}
    </>
  );
  const shape = 'flex min-h-11 desk:min-h-9 items-center gap-3 py-1.5';
  return (
    <li className="border-b border-navy/8 last:border-b-0">
      {row.link ? (
        <Link to={row.link} className={shape}>
          {inner}
        </Link>
      ) : (
        <div className={shape}>{inner}</div>
      )}
      {row.kind === 'call' && view === 'office' ? (
        <div className="pb-2">
          <DiscardControl
            idPrefix={`call-${row.id}`}
            what="this call"
            doneSentence="Discarded. It is off their page, and why is on the record."
            hint="It leaves their page. The record and the reason stay, and the phone system keeps its own."
            onDiscard={(body: Discard) => discardClientCall(clientId, row.id, body)}
            onDone={onDiscarded}
          />
        </div>
      ) : null}
    </li>
  );
}
